import type { Operation } from 'signify-ts'

import {
  CreateRegistryArgs,
  Saider,
  SignifyClient,
  Tier,
  Authenticater,
  Signer
} from 'signify-ts'
import constants from '@/config/constants'

import { GroupAid, Identifier } from '@/state/signify'
import { IOOBI } from '@/types/SignifyTypes'
import {
  getFirstWitnessFromEnds,
  getLocalMemberFromGroupAid,
  isGroupAid
} from '@/utils/aid'
import { consoleLog } from '@/utils/console-logger'
import {
  getIdentifierTypeFromAlias,
  waitOperationWithRetries
} from './wallet/util'
import {
  formatUrlPath,
  hasKey,
  isUrlBlacklistedForSignedHeaders
} from '@/utils/common'
import { GroupMember } from './wallet/types'
import { CreateNativeAidRequest } from '@/api/origin-agent-svc'

// aidHab is the result of `client.identifiers().get(alias)` — the signify-ts
// SDK returns it as `any`, and downstream `signHeaders` accepts it as-is.
// Typing the parameter as `unknown` here forces a clean cast at the boundary
// without leaking `any` across the module.
const attachInterceptor = (signifyClient: SignifyClient, aidHab: unknown) => {
  const originalFetch = fetch

  const fetchWithCustomHeader = async (
    url: RequestInfo | URL,
    options: RequestInit | undefined
  ): Promise<Response> => {
    const optionsWithCustomHeader = { ...options }
    const headers = optionsWithCustomHeader?.headers || {}

    let rawBody: string | ArrayBuffer | undefined = undefined
    const body = optionsWithCustomHeader?.body

    if (typeof body === 'string') {
      rawBody = body
    } else if (body instanceof Blob) {
      rawBody = await body.arrayBuffer()
    }

    let path = ''
    // The signed `@path` component MUST match the server's verification base.
    // origin-auth-server-lib reconstructs `@path` from
    // HttpServletRequest.getRequestURI() — the pathname WITHOUT the query string —
    // so we sign (and blacklist-check) the pathname only. Reverts DF-2797/#855,
    // which signed pathname+search and thereby diverged from the verifier, breaking
    // every query-bearing signed request ("Signature is not valid"). Covering the
    // query in the signature is a coordinated cross-service change, deferred —
    // see this.i 4u7bhm24.
    try {
      path = new URL(url.toString()).pathname
    } catch (error) {
      //ignoring -next urls
    }

    let modifiedHeaders = new Headers()

    if (!(headers instanceof Headers))
      Object.keys(headers).forEach((key) => {
        // `headers` is not a Headers instance here; it's a plain object (string[][]) or Record<string,string>.
        modifiedHeaders.set(key, (headers as Record<string, string>)[key])
      })
    else {
      for (const [key, value] of headers.entries())
        modifiedHeaders.set(key, value)
    }

    if (
      !modifiedHeaders.has('Signify-Resource') &&
      !isUrlBlacklistedForSignedHeaders(path)
    ) {
      modifiedHeaders = await signHeaders(
        signifyClient,
        aidHab,
        // RequestInit.method is optional; default to GET when omitted.
        optionsWithCustomHeader.method ?? 'GET',
        path,
        modifiedHeaders,
        rawBody
      )
    }

    optionsWithCustomHeader.headers = modifiedHeaders

    return originalFetch(url, optionsWithCustomHeader)
  }

  window.fetch = fetchWithCustomHeader
}

export const initWallet = async (passCode: string) => {
  const { ready, SignifyClient } = await import('signify-ts')
  await ready()
  const client = new SignifyClient(constants.KERIA_URL, passCode)

  const [evt, sign] = client.controller?.event ?? []
  return {
    icp: evt.sad,
    sig: sign.qb64,
    stem: client.controller?.stem,
    pidx: 1,
    tier: client.controller?.tier
  }
}

export const initClient = async (passCode: string, originAid?: string) => {
  const { ready, SignifyClient } = await import('signify-ts')
  await ready()
  const client = new SignifyClient(
    constants.KERIA_URL,
    passCode,
    Tier.low,
    constants.SIGNIFY_BOOT
  )
  let userAidHab = null
  let resolvedOriginAid = originAid
  try {
    await client.connect()

    if (!resolvedOriginAid) {
      if (!constants.WITNESS_AIDS) {
        throw new Error('Witnesses are not configured for the identifiers')
      }
      const witnessAids = constants.WITNESS_AIDS.split(',').map((item) =>
        item.trim()
      )
      // The following delay is required to let KERIA finish booting the wallet
      // and resolving OOBIs before creating any identifier
      await new Promise((resolve) => setTimeout(resolve, 3000))
      const op: any = await createIdentifier(
        client,
        'me-as-user-at-origin',
        witnessAids
      )
      resolvedOriginAid = op.i
    }
    const aids = await client.identifiers().list()
    const userAid = aids.aids.find(
      (aid: { prefix: string }) => aid.prefix === resolvedOriginAid
    )
    // Guard: if KERIA returns no matching AID we'd previously crash on
    // userAid.name. Skip the hab-fetch & interceptor wiring in that case;
    // the caller checks `userAidHab` for null before relying on it.
    if (userAid) {
      userAidHab = await client.identifiers().get(userAid.name)
      attachInterceptor(client, userAidHab)
    }
  } catch (ex) {
    console.error('INIT_CLIENT_ERROR::: ' + ex)
  }
  return { client, userAidHab, originAid: resolvedOriginAid }
}

export const getIdentifiers = async (
  client: SignifyClient,
  start: number = 0,
  end: number = 100
): Promise<Identifier[]> => {
  let result = await client.identifiers().list(start, end)
  let identifiers: Identifier[] = []
  for (let i = 0; i < result?.['aids']?.length; i++) {
    const identif: Identifier = result['aids'][i]
    let isGroup = isGroupAid(identif)
    let group: GroupAid | undefined = undefined
    let rawData = null
    if (isGroup === true) {
      rawData = await client.identifiers().get(identif.name)
      let members = await getGroupMembersSigningThreshold(client, identif.name)
      let localAid = getLocalMemberFromGroupAid(identif)
      let witness = getFirstWitnessFromEnds(
        members?.find((x) => x.aid === localAid?.prefix)?.ends
      )
      // formatUrlPath returns `string | null`; the GroupAid.witnessOobi field
      // is `string | undefined`. Normalise null → undefined so we don't widen
      // the GroupAid type just to carry a transient null.
      const witnessOobi = witness?.http
        ? formatUrlPath(witness.http, `oobi/${identif.prefix}`)
        : undefined
      group = {
        name: identif.name,
        prefix: identif.prefix,
        localAid: localAid,
        members: members ?? undefined,
        minSignersThreshold:
          members && members.length > 0 ? members[0].minSignersThreshold : 0,
        witnessOobi: witnessOobi ?? undefined
      }
    }

    const identifier: Identifier = {
      name: identif.name,
      prefix: identif.prefix,
      isMultisig: isGroup,
      group: group,
      raw: rawData
    }

    identifiers.push(identifier)
  }
  return identifiers
}

export const getIdentifier = async (client: SignifyClient, alias: string) => {
  return await client.identifiers().get(alias)
}

export const createIdentifier = async (
  client: SignifyClient,
  alias: string,
  witnessAids: string[]
) => {
  if (witnessAids.length === 0) {
    throw new Error('Witnesses are not configured for the identifiers')
  }

  let args: any = {
    wits: witnessAids,
    toad: Number(constants.WITNESS_THRESHOLD) || witnessAids.length //  witnessAids.length
  }

  let res = await client.identifiers().create(alias, args)
  // Poll the witness/inception operation every 5s, up to 60s (12 tries). The
  // previous `(5, 60000)` polled once at ~0ms then SLEPT 60s before retrying, so
  // a healthy inception (witnessed in ~2s) wasn't observed until the 60s mark —
  // the create-identifier toast appeared a full minute late and timed out any
  // 60s waiter. A 5s cadence observes completion within ~5s of the receipts
  // landing without hammering KERIA once per second.
  let op = await waitOperationWithRetries(client, await res.op(), 12, 5000)

  await addEndRole(client, alias, client!.agent!.pre, 'agent')
  await createRegistry(client, alias, `reg-${alias}`)

  return (op as { response?: unknown })['response']
}

export const renameIdentifier = async (
  client: SignifyClient,
  alias: string,
  newAlias: string
): Promise<any> => {
  return await client.identifiers().update(alias, { name: newAlias })
}

export const deleteIdentifier = async (
  client: SignifyClient,
  alias: string
): Promise<any> => {
  // Didn't make it into signify- v0.3.0
  // https://github.com/WebOfTrust/signify-ts/pull/241/files
  // @ts-expect-error: cd-0026 — signify-ts type for identifiers().delete not yet shipped in v0.3.0
  return await client.identifiers().delete(alias)
}

export const addEndRole = async (
  client: SignifyClient,
  alias: string,
  prefix: string,
  role: string
) => {
  try {
    let res = await client.identifiers().addEndRole(alias, role, prefix)
    await waitOperationWithRetries(client, await res.op(), 30)
    const oobiRes = await client.oobis().get(alias, role)
    if (oobiRes && oobiRes?.oobis.length < 1) {
      await client.identifiers().addEndRole(alias, role, prefix)
    }
  } catch (ex) {
    console.error('Error in addEndRole', ex)
    throw ex
  }
}

export const getOobis = async (
  client: SignifyClient,
  alias: string,
  role: string = 'agent'
): Promise<IOOBI> => {
  return await client.oobis().get(alias, role)
}

export const getKeyState = async (
  client: SignifyClient,
  identifier: string
) => {
  return await client.keyStates().get(identifier)
}

export const resolveOobi = async (
  client: SignifyClient,
  oobi: string,
  name: string,
  maxRetries: number = 50
) => {
  let operation = await client.oobis().resolve(oobi, name)
  let op = await waitOperationWithRetries(client, operation, maxRetries)
  return (op as { response?: unknown })['response']
}

export const getContacts = async (
  client: SignifyClient,
  group?: string,
  filterField?: string,
  filterValue?: string
) => {
  return await client.contacts().list(group, filterField, filterValue)
}

export const getContact = async (client: SignifyClient, identifier: string) => {
  return await client.contacts().get(identifier)
}

export const addContact = async (
  client: SignifyClient,
  identifier: string,
  info: any
) => {
  return await client.contacts().add(identifier, info)
}

export const updateContact = async (
  client: SignifyClient,
  identifier: string,
  info: any
) => {
  return await client.contacts().update(identifier, info)
}

export const deleteContact = async (
  client: SignifyClient,
  identifier: string
) => {
  return await client.contacts().delete(identifier)
}

/**
 * Generate a random challenge word list based on BIP39.
 * @async
 * @returns {Promise<any>} A promise to the list of random words
 */
export const generateChallenge = async (client: SignifyClient) => {
  return client.challenges().generate(128)
}

/**
 * Respond to a challenge by signing a message with the list of words
 * @async
 * @param {SignifyClient} client Signify Client
 * @param {string} alias Alias of the AID of the respondent
 * @param {string} recipientAid AID of the recipient of the response
 * @param {Array<string>} words List of words to embed in the signed response
 * @returns {Promise<Response>} A promise to the result of the response
 */
export const respondToChallenge = async (
  client: SignifyClient,
  alias: string,
  recipientAid: string,
  words: string[]
) => {
  return client.challenges().respond(alias, recipientAid, words)
}

/**
 * Ask Agent to verify a given sender signed the provided words
 * @param {SignifyClient} client Signify Client
 * @param {string} alias Name or alias of the identifier
 * @param {string} source AID that was challenged
 * @param {Array<string>} words List of challenge words to check for
 * @returns {Promise<Response>} A promise to the result
 */
export const verifyChallengeResponse = async (
  client: SignifyClient,
  alias: string,
  pre: string,
  words: string[]
) => {
  consoleLog('verify challenge response using aid  ' + alias)
  let operation = (await client.challenges().verify(pre, words)) as Operation
  const operationsInstance = client.operations()
  while (!operation['done']) {
    operation = await operationsInstance.get(operation['name'])
    await new Promise((resolve) => setTimeout(resolve, 1000))
  }
}

/**
 * Mark challenge response as signed and accepted
 * @param {SignifyClient} client Signify Client
 * @param {string} name Name or alias of the identifier
 * @param {string} source AID that was challenged
 * @param {string} said qb64 AID of exn message representing the signed response
 * @returns {Promise<Response>} A promise to the result
 */
export const acceptChallengeResponse = async (
  client: SignifyClient,
  alias: string,
  pre: string,
  said: string
) => {
  consoleLog('accept challenge response using aid ' + alias)
  return await client.challenges().responded(pre, said)
}

export const getSchemas = async (client: SignifyClient) => {
  return await client.schemas().list()
}

export const getSchema = async (client: SignifyClient, said: string) => {
  return await client.schemas().get(said)
}

export const getRegistries = async (client: SignifyClient, name: string) => {
  return await client.registries().list(name)
}

export const createRegistry = async (
  client: SignifyClient,
  alias: string,
  registryName: string,
  nonce?: string
) => {
  let args: CreateRegistryArgs = {
    name: alias,
    registryName: registryName,
    nonce: nonce
  }
  let res = await client.registries().create(args)
  let op = await waitOperationWithRetries(client, await res.op(), 60)
  return (op as { response?: unknown })['response']
}

export const getCredentials = async (
  client: SignifyClient,
  filter: object,
  limit: number = 100
): Promise<any[]> => {
  if (filter !== undefined && filter !== null)
    return await client.credentials().list({ filter: filter, limit: limit })
  else return await client.credentials().list({ limit: limit })
}

export const issueCredential = async (
  client: SignifyClient,
  alias: string,
  registy: string,
  recipient: string,
  schema: string,
  credentialData: any,
  rules: any | undefined,
  source: any | undefined,
  _private?: boolean
) => {
  return await client.credentials().issue(alias, {
    ri: registy,
    s: schema,
    a: { i: recipient, ...credentialData },
    r: rules && Saider.saidify({ d: '', rules })[1],
    e: source && Saider.saidify({ d: '', source })[1]
  })
}

export const requestCredential = async (
  client: SignifyClient,
  alias: string,
  recipient: string,
  schema: string,
  issuer: string
) => {
  return await client
    .ipex()
    .apply({ senderName: alias, recipient: recipient, schemaSaid: schema })
}

export const getNotifications = async (
  client: SignifyClient,
  start: number = 0,
  end: number = 200
) => {
  return await client.notifications().list(start, end)
}

export const markNotification = async (client: SignifyClient, said: string) => {
  return await client.notifications().mark(said)
}

export const deleteNotification = async (
  client: SignifyClient,
  said: string
) => {
  return await client.notifications().delete(said)
}

export const getGroupMembersSigningThreshold = async (
  client: SignifyClient,
  alias: string
): Promise<GroupMember[] | null> => {
  // Return type is `GroupMember[] | null` (not `GroupMember[]`): the
  // non-group-aid path returns null intentionally so callers can distinguish
  // "not a group aid" from "group aid with zero members" (test:
  // services/__tests__/signify.spec.ts §getGroupMembersSigningThreshold).
  let aid = await client.identifiers().get(alias)
  if (hasKey(aid, 'group')) {
    let signingThreshold = aid?.state?.kt
    let minSigners = findMinimumSignersThreshold(signingThreshold)
    let members = await client.identifiers().members(alias)

    // signify-ts returns members.signing as `any[]`; we shape it into
    // GroupMember locally. Annotate the callback so noImplicitAny is happy
    // without leaking `any` across the function boundary.
    const groupMembers: GroupMember[] =
      members?.signing?.map(
        (item: { aid: string; ends: unknown }, index: number) =>
          ({
            aid: item.aid,
            ends: item.ends,
            weightage: Array.isArray(signingThreshold)
              ? signingThreshold[index]
              : null,
            weighted: Array.isArray(signingThreshold),
            minSignersThreshold: minSigners
          }) as GroupMember
      ) ?? []

    groupMembers.sort((a, b) => a.aid.localeCompare(b.aid))
    return groupMembers
  } else return null
}

/**
 * Calculates the minimum threshold from a given current signing threshold(kt), which can be either a single string or an array of strings and numbers.
 * The function determines the smallest set of elements from the array whose sum is equal to or exceeds 1.
 *
 * @param {string | (string | number)[]} kt - current signing threshold(kt), it can be a number, or an array of fractions.
 * @returns {number} The minimum number of members of group AID that must sign.
 *
 * Example Usage:
 * findMinSignersThreshold("2"); // returns 2
 * findMinSignersThreshold(["1/2", "1/4", "1", "3"]); // returns 1
 * findMinSignersThreshold(["1/3", "1/3", "1/3", "1/3"]); // returns 3
 */
export function findMinimumSignersThreshold(
  kt: string | (string | number)[]
): number {
  try {
    if (typeof kt === 'string') {
      return Number(kt)
    }

    const numericalFractions = kt.map((fraction) => {
      if (typeof fraction === 'number') {
        return fraction
      } else if (typeof fraction === 'string' && fraction.includes('/')) {
        const [numerator, denominator] = fraction.split('/').map(Number)
        return numerator / denominator
      } else {
        return Number(fraction)
      }
    })

    numericalFractions.sort((a, b) => b - a)

    let sum = 0
    let threshold = 0

    for (const fraction of numericalFractions) {
      sum += fraction
      threshold++
      if (sum >= 1) {
        break
      }
    }

    return threshold
    // Returns -1 sentinel so callers can detect parse failure via `result > 0`.
  } catch (x) {
    return -1
  }
}

export const generateAgentSvcIdentifierPayload = async (
  signifyClient: SignifyClient,
  alias: string,
  userId: string,
  orgId: string,
  role: string = 'agent'
) => {
  const aidResult = await getIdentifier(signifyClient, alias)

  const oobiResult = await getOobis(signifyClient, alias, role)

  const payload: CreateNativeAidRequest = {
    aid: aidResult.prefix,
    alias: aidResult.name,
    oobi: oobiResult.oobis,
    orgId: orgId,
    type: getIdentifierTypeFromAlias(aidResult.name, userId),
    isMultisig: false,
    userId: userId
  }

  return payload
}

export const getAidDetail = async (
  signifyClient: SignifyClient,
  alias: string,
  role: string = 'agent'
) => {
  const aidResult = await getIdentifier(signifyClient, alias)
  let isGroup = isGroupAid(aidResult)
  const oobiResult = await getOobis(signifyClient, alias, role)
  const aidDetail = {
    aid: aidResult.prefix,
    alias: aidResult.name,
    oobi: oobiResult.oobis,
    isMultisig: isGroup
  }
  return aidDetail
}

export const signHeaders = async (
  client: SignifyClient,
  aidHab: any,
  method: string,
  path: string,
  headers: Headers,
  body: string | ArrayBuffer | undefined
): Promise<Headers> => {
  const keeper = client?.manager!.get(aidHab)
  const signer = new Signer({ raw: keeper.signers[0].raw })
  const authenticator = new Authenticater(signer, signer.verfer)
  headers.set('Signify-Resource', aidHab.prefix)
  headers.set('origin-date', new Date().toISOString().replace('Z', '000+00:00'))

  let dataToHash: ArrayBuffer
  if (!body) {
    dataToHash = new TextEncoder().encode('').buffer
  } else if (typeof body === 'string') {
    dataToHash = new TextEncoder().encode(body).buffer
  } else if (body instanceof ArrayBuffer) {
    dataToHash = body
  } else if (ArrayBuffer.isView(body)) {
    // See: https://stackoverflow.com/a/79345743
    dataToHash = new Uint8Array(body).buffer
  } else {
    throw new Error('Unsupported body type for hashing')
  }

  const buffer = await crypto.subtle.digest('SHA-256', dataToHash)

  const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')

  const digest = `sha-256=:${base64}:`
  headers.set('content-digest', digest)

  const fields = [
    '@method',
    '@path',
    'origin-date',
    'signify-resource',
    'content-digest'
  ]

  const signed_headers = authenticator.sign(headers, method, path, fields)
  return signed_headers
}
